import React, { useState } from 'react';
import { toast } from 'sonner';
import { Trash2, UserPlus, Check, Eye, UserCog, RotateCcw } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { adminAddInvestor, adminRemoveInvestor, adminApproveKyc, adminKycDocumentBlob, adminSeedDemo } from '../../lib/api';
import InvestorDialog from '../InvestorDialog';
import { inputCls, KYC_BADGE_CLS } from './shared';

export default function InvestorsTab({ investors, onChanged }) {
  const [inv, setInv] = useState({ email: '', name: '' });
  const [manageId, setManageId] = useState(null);
  const [seedingDemo, setSeedingDemo] = useState(false);

  const addInvestor = async (e) => {
    e.preventDefault();
    if (!inv.email) return toast.error('Enter an email');
    try {
      const r = await adminAddInvestor(inv);
      toast.success(r.emailed ? 'Investor onboarded. Invite email sent' : 'Investor onboarded');
      setInv({ email: '', name: '' }); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const removeInvestor = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await adminRemoveInvestor(u.user_id); toast.success('Removed'); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const approveKyc = async (u) => {
    try { await adminApproveKyc(u.user_id); toast.success('KYC approved'); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const viewKycDoc = async (u) => {
    try { const blob = await adminKycDocumentBlob(u.user_id); window.open(URL.createObjectURL(blob), '_blank'); }
    catch { toast.error('Could not load KYC document'); }
  };
  const seedDemo = async () => {
    if (!window.confirm('Reset the demo@farmway.io showcase data? Existing demo holdings, listings, bids and documents will be recreated.')) return;
    setSeedingDemo(true);
    try {
      const r = await adminSeedDemo();
      if (r.warnings?.length) toast.warning(r.warnings[0]);
      toast.success(`Demo data ready: ${r.holdings} holdings ($${Number(r.invested).toLocaleString()} → $${Number(r.currentValue).toLocaleString()}, +${r.returnPct}%), ${r.listings} live listings, ${r.notifications} alerts${r.documents ? `, ${r.documents} documents` : ''}`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Seeding failed'); }
    finally { setSeedingDemo(false); }
  };

  return (
    <>
      <form onSubmit={addInvestor} className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-5">
        <div className="space-y-1.5"><Label className="text-farmway-cream/80">Investor email</Label><Input value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} placeholder="investor@email.com" className={inputCls} /></div>
        <div className="space-y-1.5"><Label className="text-farmway-cream/80">Name (optional)</Label><Input value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className={inputCls} /></div>
        <button type="submit" className="btn-gold"><UserPlus className="h-4 w-4" /> Onboard investor</button>
      </form>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-farmway-gold/30 bg-farmway-gold/5 p-4">
        <p className="text-xs text-farmway-cream/60">VC showcase account <b className="text-farmway-cream">demo@farmway.io</b>: resets holdings, live listings & bids, trade history, documents and alerts to a polished demo portfolio.</p>
        <button data-testid="seed-demo-btn" onClick={seedDemo} disabled={seedingDemo} className="btn-outline shrink-0 disabled:opacity-70"><RotateCcw className="h-4 w-4" /> {seedingDemo ? 'Seeding…' : 'Reset demo data'}</button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/10">
        {investors.length === 0 ? <p className="px-6 py-8 text-farmway-cream/50">No investors yet.</p> : investors.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between gap-4 border-t border-white/10 px-6 py-4 first:border-t-0">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-farmway-cream">
                {u.name}
                <span data-testid={`investor-status-${u.user_id}`} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-farmway-cream/60">{u.status}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${KYC_BADGE_CLS[u.kyc?.status] || 'bg-white/10 text-farmway-cream/50'}`}>KYC {u.kyc?.status === 'not_started' || !u.kyc?.status ? 'pending' : u.kyc.status}</span>
              </p>
              <p className="text-xs text-farmway-cream/50">{u.email} · {u.holdingsCount} holding(s)</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button data-testid={`manage-investor-${u.user_id}`} onClick={() => setManageId(u.user_id)} className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1.5 text-[11px] font-bold uppercase text-farmway-cream/80 hover:border-farmway-gold hover:text-farmway-gold"><UserCog className="h-3 w-3" /> Manage</button>
              {u.kyc?.document && (
                <button data-testid={`view-kyc-doc-${u.user_id}`} onClick={() => viewKycDoc(u)} className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1.5 text-[11px] font-bold uppercase text-farmway-cream/80 hover:border-farmway-gold hover:text-farmway-gold"><Eye className="h-3 w-3" /> View ID</button>
              )}
              {u.kyc?.status === 'submitted' && (
                <button data-testid={`kyc-approve-${u.user_id}`} onClick={() => approveKyc(u)} className="inline-flex items-center gap-1 rounded-full bg-farmway-gold px-3 py-1.5 text-[11px] font-bold uppercase text-farmway-green"><Check className="h-3 w-3" /> Approve KYC</button>
              )}
              <button data-testid={`remove-investor-${u.user_id}`} onClick={() => removeInvestor(u)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/15 text-farmway-cream/70 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <InvestorDialog userId={manageId} open={Boolean(manageId)} onOpenChange={(o) => !o && setManageId(null)} onChanged={onChanged} />
    </>
  );
}
